import { useEffect, useState } from 'react';
import axios from 'axios';
import { History, Loader2, Lock, ShoppingCart } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { InvitationPreview } from '../InvitationPreview';
import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';
import { formatPrice } from '../../utils/format';
import { cn } from '../../lib/utils';
import { FOCUS_RING, PILL_INK, PILL_OUTLINE } from './constants';
import { Segmented } from './controls';

const DIALOG = 'bg-paper rounded-[24px] border-0 p-6 sm:p-7 max-w-[calc(100vw-24px)]';

const relativeTime = (iso) => {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = Math.max(0, Date.now() - t) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Version history: GET /invitations/{id}/versions, restore one. */
export const VersionsDialog = ({ open, onOpenChange, invitationId, dirty, onRestored }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    if (!open || !invitationId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(`${API_URL}/invitations/${invitationId}/versions`)
      .then(({ data }) => !cancelled && setVersions(Array.isArray(data) ? data : []))
      .catch((err) => !cancelled && setError(getErrorMessage(err, 'We could not load the version history.')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, invitationId]);

  const restore = async (version) => {
    if (dirty && !window.confirm('Restoring replaces your unsaved changes. Continue?')) return;
    setRestoring(version.id);
    try {
      const { data } = await axios.post(`${API_URL}/invitations/${invitationId}/versions/${version.id}/restore`);
      onRestored(data);
      onOpenChange(false);
    } catch (err) {
      setError(getErrorMessage(err, 'We could not restore that version.'));
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(DIALOG, 'sm:max-w-md')} data-testid="versions-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-serif text-2xl font-semibold flex items-center gap-2">
            <History className="h-5 w-5" aria-hidden="true" /> Versions
          </DialogTitle>
          <DialogDescription className="text-[#4A4640]">
            Every save keeps the previous version for 7 days. Restoring saves your current design as a version first.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1 flex flex-col gap-2" aria-live="polite">
          {loading && (
            <p className="text-sm text-[#6B665C] flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading versions…
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-[#B0003A]">
              {error}
            </p>
          )}
          {!loading && !error && versions.length === 0 && (
            <p className="text-sm text-[#6B665C]">No earlier versions yet. They appear here after you save changes.</p>
          )}
          {versions.map((v) => (
            <div key={v.id} className="rounded-xl bg-white border border-[#E6E1D6] px-3.5 py-2.5 flex items-center gap-3" data-testid={`version-${v.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-ink">{relativeTime(v.created_at)}</p>
                <p className="text-xs text-[#6B665C] truncate">
                  {v.name}
                  {typeof v.layers === 'number' ? ` · ${v.layers} layer${v.layers === 1 ? '' : 's'}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => restore(v)}
                disabled={Boolean(restoring)}
                className={cn(PILL_OUTLINE, 'h-9 px-4 text-[13px] gb-paper')}
                data-testid={`restore-${v.id}`}
              >
                {restoring === v.id ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const WallFrame = ({ children }) => (
  <div className="relative bg-gradient-to-br from-[#F9F8F6] to-[#DCD6C8] rounded-[24px] p-8 flex flex-col items-center justify-center min-h-[420px]">
    <div className="bg-amber-900/80 p-3 rounded shadow-2xl" style={{ transform: 'perspective(600px) rotateY(-5deg)' }}>
      <div className="w-[240px] bg-white shadow-lg">{children}</div>
    </div>
    <p className="mt-6 text-center text-xs font-semibold text-[#6B665C]">Wall frame preview</p>
  </div>
);

const PhoneFrame = ({ children }) => (
  <div className="flex flex-col items-center justify-center min-h-[420px]">
    <div className="bg-gradient-to-b from-gray-700 to-gray-900 rounded-3xl p-3 shadow-xl w-[220px]">
      <div className="bg-gray-800 rounded-2xl p-2">
        <div className="bg-green-600 rounded-t-lg px-2 py-1 flex items-center gap-2">
          <div className="w-6 h-6 bg-white/20 rounded-full" />
          <span className="text-white text-xs font-medium">Event Invite</span>
        </div>
        <div className="bg-gray-100 p-2">
          <div className="bg-white rounded-lg shadow overflow-hidden">{children}</div>
        </div>
      </div>
    </div>
    <p className="text-center text-xs font-semibold text-[#6B665C] mt-4">Mobile message preview</p>
  </div>
);

/** Free preview: the page, a wall frame and a phone message. */
export const PreviewDialog = ({ open, onOpenChange, design, backgroundImage, perspective, showWatermark, initialMode = 'page', footer }) => {
  const [mode, setMode] = useState(initialMode);
  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);
  const preview = (
    <InvitationPreview
      backgroundImage={backgroundImage}
      perspective={perspective}
      fields={design.text_fields}
      elements={design.elements}
      background={design.background}
      showWatermark={showWatermark}
      eager
      alt="Your invitation"
    />
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(DIALOG, 'sm:max-w-xl max-h-[calc(100vh-24px)] overflow-y-auto')} data-testid="preview-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-serif text-2xl font-semibold">Free preview</DialogTitle>
          <DialogDescription className="text-[#4A4640]">This is how your invitation will look. Previews are free.</DialogDescription>
        </DialogHeader>
        <Segmented
          label="Preview"
          options={[
            ['page', 'Page'],
            ['wall', 'Wall frame'],
            ['phone', 'Mobile'],
          ]}
          value={mode}
          onChange={setMode}
        />
        {mode === 'page' && <div className="mx-auto w-full max-w-[380px] shadow-[0_30px_60px_-24px_rgba(26,26,26,0.45)]">{preview}</div>}
        {mode === 'wall' && <WallFrame>{preview}</WallFrame>}
        {mode === 'phone' && <PhoneFrame>{preview}</PhoneFrame>}
        {footer}
      </DialogContent>
    </Dialog>
  );
};

/** Shown when a download needs a purchase (unpaid, or 402 from the server). */
export const PurchaseDialog = ({ open, onOpenChange, price, canPurchase, busy, onAddToCart, onBuyNow, reason }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={cn(DIALOG, 'sm:max-w-md')} data-testid="purchase-dialog">
      <DialogHeader className="text-left">
        <DialogTitle className="font-serif text-2xl font-semibold flex items-center gap-2">
          <Lock className="h-5 w-5 text-[#6E5B1C]" aria-hidden="true" /> Unlock downloads
        </DialogTitle>
        <DialogDescription className="text-[#4A4640]">
          {reason || 'Purchase this invitation to download it without the watermark, including print quality (4K).'}
        </DialogDescription>
      </DialogHeader>
      <div className="gradient-border gb-cream rounded-[18px] p-5 flex flex-col gap-3">
        <div className="flex justify-between items-baseline gap-2">
          <span className="font-bold text-ink">Digital copy</span>
          <span className="font-serif text-2xl font-semibold text-ink">{formatPrice(price)}</span>
        </div>
        <p className="text-sm text-[#4A4640] -mt-1">PDF, PNG and JPG, plus a 4K print-quality file. Watermark-free.</p>
        {!canPurchase && <p className="text-sm text-[#6B665C]">Save your invitation first.</p>}
        <div className="flex flex-col sm:flex-row gap-2">
          <button type="button" onClick={onBuyNow} disabled={!canPurchase || busy} className={cn(PILL_INK, 'h-11 flex-1 px-4 text-sm')} data-testid="buy-now-btn">
            {busy ? 'Adding…' : 'Buy now'}
          </button>
          <button
            type="button"
            onClick={onAddToCart}
            disabled={!canPurchase || busy}
            className={cn(PILL_OUTLINE, 'h-11 flex-1 px-4 text-sm gb-cream')}
            data-testid="add-digital-btn"
          >
            <ShoppingCart className="h-4 w-4" aria-hidden="true" /> Add to cart
          </button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const NameField = ({ value, onChange, className }) => (
  <input
    id="invitation-name"
    aria-label="Invitation name"
    value={value}
    maxLength={120}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
    }}
    className={cn(
      'min-w-0 bg-transparent font-extrabold text-[15px] text-ink rounded-md px-1 -mx-1 hover:bg-cream focus:bg-white outline-none',
      FOCUS_RING,
      className
    )}
    data-testid="invitation-name-input"
  />
);

const SHORTCUTS = [
  ['Editing', [
    ['Ctrl + Z', 'Undo'],
    ['Ctrl + Y  /  Ctrl + Shift + Z', 'Redo'],
    ['Ctrl + S', 'Save'],
    ['Ctrl + C / X / V', 'Copy / cut / paste'],
    ['Ctrl + D', 'Duplicate'],
    ['Delete', 'Delete (template lines are hidden)'],
  ]],
  ['Selecting', [
    ['Click', 'Select one item'],
    ['Shift + click', 'Add or remove from the selection'],
    ['Drag on an empty spot', 'Select everything inside the box'],
    ['Ctrl + A', 'Select all'],
    ['Esc', 'Clear the selection'],
  ]],
  ['Moving & arranging', [
    ['Arrows', 'Move a little'],
    ['Shift + arrows', 'Move more'],
    ['Alt + arrows', 'Move very finely'],
    ['Ctrl + ]  /  Ctrl + [', 'Bring forward / send back'],
    ['Ctrl + L', 'Lock / unlock'],
  ]],
  ['Text', [
    ['Ctrl + B / I / U', 'Bold / italic / underline'],
    ['Ctrl + Shift + L / E / R', 'Align left / centre / right'],
    ['Ctrl + Shift + >  /  <', 'Bigger / smaller text'],
  ]],
  ['View', [['Ctrl + mouse wheel', 'Zoom'], ['?', 'This list']]],
];

/** Keyboard shortcuts (opened with "?"). Shortcuts work with any keyboard language. */
export const ShortcutsDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={cn(DIALOG, 'sm:max-w-lg max-h-[calc(100vh-24px)] overflow-y-auto')}>
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl font-semibold">Keyboard shortcuts</DialogTitle>
        <DialogDescription className="text-[#4A4640]">On a Mac use ⌘ instead of Ctrl. They work with a Yiddish/Hebrew keyboard too.</DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-4" data-testid="shortcuts-list">
        {SHORTCUTS.map(([group, rows]) => (
          <section key={group}>
            <h3 className="text-xs font-extrabold uppercase tracking-wide text-[#6B665C] mb-1.5">{group}</h3>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              {rows.map(([keys, what]) => (
                <div key={keys} className="contents">
                  <dt>
                    <kbd className="px-1.5 py-0.5 rounded-md border border-[#DCD6C8] bg-white text-[12px] font-bold whitespace-nowrap">{keys}</kbd>
                  </dt>
                  <dd className="text-ink">{what}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);
